import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BOOKS, AI_BOOKS, UPCOMING_BOOKS, SERIES } from "../../data/content";
import BookCard from "./BookCard";

const SERIES_CONFIG = {
  finance: {
    id: "finance",
    number: "01",
    name: "Finance for Little Hands",
    heading: (
      <>
        Three books.{" "}
        <em className="italic" style={{ color: "var(--accent-orange)" }}>
          One little economist.
        </em>
      </>
    ),
    books: BOOKS,
    formats: "Paperback + Kindle eBooks",
    totalInSeries: 5,
    showUpcoming: true,
  },
  ai: {
    id: "ai",
    number: "02",
    name: "AI for Little Hands",
    heading: (
      <>
        Three books.{" "}
        <em className="italic" style={{ color: "var(--accent-orange)" }}>
          One curious mind for the machines.
        </em>
      </>
    ),
    books: AI_BOOKS,
    formats: "Paperback",
    totalInSeries: 3,
    showUpcoming: false,
    tagline:
      "Helping young minds grow up with the smartest tools of their generation, gently and thoughtfully.",
  },
};

function seriesFromHash() {
  if (typeof window === "undefined") return "finance";
  const h = (window.location.hash || "").toLowerCase();
  if (h.includes("books-ai") || h.includes("ai-series")) return "ai";
  return "finance";
}

export default function BooksShowcase({ country, countryStatus }) {
  const [active, setActive] = useState("finance");

  // React to hash changes so nav links + header BUY menu can drive the series
  useEffect(() => {
    setActive(seriesFromHash());
    const onHash = () => setActive(seriesFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const cfg = SERIES_CONFIG[active];

  const handleTab = (id) => {
    setActive(id);
    // Update hash without triggering a full jump; keep books in view
    const el = document.getElementById("books");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", `#books-${id}`);
    }
  };

  return (
    <section id="books" className="section" data-testid="books-section">
      {/* Anchor targets so header/BUY menu can scroll here per-series */}
      <span id="books-finance" className="sr-only" aria-hidden="true" />
      <span id="books-ai" className="sr-only" aria-hidden="true" />
      <span id="ai-series" className="sr-only" aria-hidden="true" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Section eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mb-8 md:mb-10"
        >
          <p className="folio-num">The Bookshelf · Choose a series</p>
        </motion.div>

        {/* Series tabs */}
        <div
          role="tablist"
          aria-label="Book series"
          className="flex flex-wrap items-stretch gap-3 md:gap-4 border-b border-stone-900/15 pb-0"
          data-testid="series-tabs"
        >
          {SERIES.map((s) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTab(s.id)}
                data-testid={`series-tab-${s.id}`}
                className={`relative flex-1 md:flex-none min-w-[260px] text-left px-5 py-4 md:px-7 md:py-5 border border-b-0 transition-colors duration-300 ${
                  isActive
                    ? "bg-[color:var(--ink)] text-[color:var(--paper)] border-[color:var(--ink)]"
                    : "bg-transparent text-[color:var(--ink)] border-stone-900/25 hover:bg-stone-900/5"
                }`}
                style={{ borderRadius: 0 }}
              >
                <span
                  className={`block text-[10px] tracking-[0.28em] uppercase ${
                    isActive ? "opacity-70" : "text-stone-500"
                  }`}
                >
                  Series № {s.number}
                </span>
                <span className="mt-1 font-serif italic text-2xl md:text-[28px] leading-none block">
                  {s.name}
                </span>
                <span
                  className={`mt-2 block text-[11px] tracking-[0.22em] uppercase ${
                    isActive ? "opacity-70" : "text-stone-500"
                  }`}
                >
                  {s.note}
                </span>
                {isActive && (
                  <motion.span
                    layoutId="series-tab-underline"
                    className="absolute left-0 right-0 -bottom-[1px] h-[3px]"
                    style={{ background: "var(--accent-orange)" }}
                    transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Series content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={cfg.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="pt-16 md:pt-20"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24"
            >
              <div className="col-span-12 md:col-span-4">
                <p className="folio-num mb-4">Series № {cfg.number}</p>
                <p
                  className="mb-4 text-[11px] uppercase tracking-[0.22em] text-stone-600"
                  data-testid={`series-format-${cfg.id}`}
                >
                  Available in {cfg.formats}
                </p>
                <div
                  className="flex items-end -space-x-4"
                  data-testid={`series-thumbs-${cfg.id}`}
                  aria-label={`${cfg.name} covers`}
                >
                  {cfg.books.map((b, idx) => (
                    <motion.img
                      key={b.id}
                      src={b.cover}
                      alt={b.title}
                      loading="lazy"
                      className="w-[90px] md:w-[110px] aspect-square object-cover shadow-[0_10px_30px_rgba(28,25,23,0.15)] border border-stone-900/10 rounded-[3px] bg-white"
                      style={{ transform: `rotate(${(idx - 1) * 4}deg)` }}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: idx * 0.08,
                        duration: 0.6,
                        ease: [0.2, 0.8, 0.2, 1],
                      }}
                    />
                  ))}
                </div>
              </div>
              <h2 className="col-span-12 md:col-span-8 font-serif text-4xl sm:text-5xl md:text-7xl leading-[0.95] tracking-tight">
                {cfg.heading}
              </h2>
              {cfg.tagline && (
                <p className="col-span-12 md:col-start-5 md:col-span-7 -mt-2 md:mt-0 text-lg leading-relaxed text-stone-700 max-w-2xl">
                  {cfg.tagline}
                </p>
              )}
            </motion.div>

            <div className="space-y-28 md:space-y-40">
              {cfg.books.map((book, i) => (
                <BookCard
                  key={book.id}
                  book={book}
                  index={i}
                  totalInSeries={cfg.totalInSeries}
                  country={country}
                  countryStatus={countryStatus}
                />
              ))}
            </div>

            {/* Coming soon — only for Finance */}
            {cfg.showUpcoming && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9 }}
                className="mt-32 md:mt-40 border-t border-stone-900/15 pt-12"
                data-testid="upcoming-books"
              >
                <div className="grid grid-cols-12 gap-6 items-end mb-10">
                  <p className="folio-num col-span-12 md:col-span-4">Volume II · Coming soon</p>
                  <h3 className="col-span-12 md:col-span-8 font-serif text-3xl md:text-5xl leading-tight tracking-tight">
                    Two more chapters, warmly in the works.
                  </h3>
                </div>
                <div className="grid grid-cols-12 gap-8">
                  {UPCOMING_BOOKS.map((b, i) => (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, duration: 0.8 }}
                      className="col-span-12 md:col-span-6 border border-stone-900/15 p-8 md:p-10 relative bg-[color:var(--paper-warm)]"
                      data-testid={`upcoming-book-${b.id}`}
                    >
                      <span className="folio-num absolute top-6 right-6">№ {b.number}</span>
                      <span className="folio-num">Coming 2026</span>
                      <h4 className="mt-6 font-serif text-3xl md:text-4xl leading-tight">{b.title}</h4>
                      <p className="mt-2 font-serif italic text-lg text-stone-600">{b.subtitle}</p>
                      <p className="mt-5 text-stone-700 leading-relaxed max-w-md">{b.hint}</p>
                      <div className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-stone-500">
                        <span className="w-8 h-px bg-stone-500" />
                        Notify me — join the list below
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
